/**
 * Category Service
 *
 * Microservice for managing subscription categories
 * Handles all database interactions for categories
 */

import { supabase } from './supabaseClient';

/**
 * Get all categories
 * @returns {Promise<Array>} Array of category objects
 */
export const getAllCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getAllCategories error:', error);
    throw error;
  }
};

/**
 * Get system categories only
 * @returns {Promise<Array>} Array of system categories
 */
export const getSystemCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('is_system', true)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching system categories:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getSystemCategories error:', error);
    throw error;
  }
};

/**
 * Get custom categories only
 * @returns {Promise<Array>} Array of custom categories
 */
export const getCustomCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('is_system', false)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching custom categories:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getCustomCategories error:', error);
    throw error;
  }
};

/**
 * Get a single category by ID
 * @param {string} categoryId - The category ID
 * @returns {Promise<Object>} Category object
 */
export const getCategoryById = async (categoryId) => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('id', categoryId)
      .single();

    if (error) {
      console.error('Error fetching category:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getCategoryById error:', error);
    throw error;
  }
};

/**
 * Get a category by name
 * @param {string} name - The category name
 * @returns {Promise<Object>} Category object
 */
export const getCategoryByName = async (name) => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('name', name)
      .single();

    if (error) {
      console.error('Error fetching category by name:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getCategoryByName error:', error);
    throw error;
  }
};

/**
 * Create a new custom category
 * @param {Object} category - Category data
 * @returns {Promise<Object>} Created category
 */
export const createCategory = async (category) => {
  try {
    const categoryData = {
      name: category.name,
      emoji: category.emoji || '📋',
      color: category.color || 'bg-gray-500',
      description: category.description || null,
      is_system: false // Custom categories are never system categories
    };

    const { data, error } = await supabase
      .from('categories')
      .insert([categoryData])
      .select()
      .single();

    if (error) {
      console.error('Error creating category:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('createCategory error:', error);
    throw error;
  }
};

/**
 * Update an existing category
 * Note: System categories cannot be updated
 * @param {string} categoryId - The category ID
 * @param {Object} updates - Updated category data
 * @returns {Promise<Object>} Updated category
 */
export const updateCategory = async (categoryId, updates) => {
  try {
    // Check if it's a system category
    const category = await getCategoryById(categoryId);
    if (category.is_system) {
      throw new Error('System categories cannot be updated');
    }

    const updateData = {
      name: updates.name,
      emoji: updates.emoji,
      color: updates.color,
      description: updates.description
    };

    // Remove undefined values
    Object.keys(updateData).forEach(key =>
      updateData[key] === undefined && delete updateData[key]
    );

    const { data, error } = await supabase
      .from('categories')
      .update(updateData)
      .eq('id', categoryId)
      .select()
      .single();

    if (error) {
      console.error('Error updating category:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('updateCategory error:', error);
    throw error;
  }
};

/**
 * Delete a category
 * Note: System categories cannot be deleted
 * @param {string} categoryId - The category ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteCategory = async (categoryId) => {
  try {
    // Check if it's a system category
    const category = await getCategoryById(categoryId);
    if (category.is_system) {
      throw new Error('System categories cannot be deleted');
    }

    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', categoryId);

    if (error) {
      console.error('Error deleting category:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('deleteCategory error:', error);
    throw error;
  }
};

/**
 * Get category usage statistics
 * Returns count of subscriptions using each category
 * @returns {Promise<Array>} Array of categories with usage counts
 */
export const getCategoryUsageStats = async () => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select(`
        id,
        name,
        emoji,
        color,
        subscriptions:subscriptions(count)
      `);

    if (error) {
      console.error('Error fetching category usage stats:', error);
      throw error;
    }

    return data.map(cat => ({
      ...cat,
      subscriptionCount: cat.subscriptions?.[0]?.count || 0
    }));
  } catch (error) {
    console.error('getCategoryUsageStats error:', error);
    throw error;
  }
};

export default {
  getAllCategories,
  getSystemCategories,
  getCustomCategories,
  getCategoryById,
  getCategoryByName,
  createCategory,
  updateCategory,
  deleteCategory,
  getCategoryUsageStats
};
